import type { Goal } from "@/models/Goal.model";

/**
 * Display order for a flat list of goals that may contain subgoals: parents sorted by
 * path, each followed by its own subgoals in the order they arrived (the mapper and the
 * API both deliver them in curriculum order). A subgoal whose parent is not in the list
 * (legacy data, a parent removed elsewhere) still renders, at the end.
 *
 * Parent and subgoal are matched on source + path: a subgoal's `sourceParentPath` is its
 * parent's `fullPath`, and paths are only unique within one curriculum source.
 */
export function groupSubgoalsUnderParents(goals: Goal[]): Goal[] {
	const byPath = (a: Goal, b: Goal) => (a.fullPath || '').localeCompare(b.fullPath || '');
	const keyOf = (source: string | undefined, path: string | undefined) => `${(source ?? '').toLowerCase()}|${path ?? ''}`;

	const parents = goals.filter((goal) => !goal.isSubgoal()).sort(byPath);
	const parentKeys = new Set(parents.map((goal) => keyOf(goal.source, goal.fullPath)));

	const subgoalsByParent = new Map<string, Goal[]>();
	const orphans: Goal[] = [];

	for (const goal of goals) {
		if (!goal.isSubgoal()) {
			continue;
		}
		const key = keyOf(goal.source, goal.sourceParentPath);
		if (!parentKeys.has(key)) {
			orphans.push(goal);
			continue;
		}
		if (!subgoalsByParent.has(key)) {
			subgoalsByParent.set(key, []);
		}
		subgoalsByParent.get(key)!.push(goal);
	}

	const rows: Goal[] = [];
	for (const parent of parents) {
		rows.push(parent, ...(subgoalsByParent.get(keyOf(parent.source, parent.fullPath)) ?? []));
	}
	return [...rows, ...orphans.sort(byPath)];
}
